/**
 * Product Chat Conversion Tracking
 * 
 * This script tracks conversions from product chat sessions by:
 * 1. Monitoring Shopify Customer Events (add to cart, checkout, purchase)
 * 2. Connecting those events to active Product Chat sessions
 * 3. Sending conversion data back to the Google Sheet
 */

// Configuration with reasonable defaults
const CONVERSION_CONFIG = {
  enabled: true,
  testMode: true, // Set to false in production
  attributionWindow: 7, // Days to attribute a conversion
  debug: true, // Enable logging
  conversionEventEndpoint: 'https://script.google.com/macros/s/AKfycbyLRtvbGxxT29bCcWNOirKteUA_l6QvDxH73dZaHpdTxcEwn3gO2MFfhn7ICWTRPnGG/exec',
  storageKey: 'productChat_conversion_data',
  productChatSelector: '.product-chat-container',
  testOrderPrefix: 'TEST_ORDER_'
};

class ProductChatConversionTracker {
  constructor(config = {}) {
    // Merge provided config with defaults
    this.config = { ...CONVERSION_CONFIG, ...config };
    
    // Don't initialize if not enabled
    if (!this.config.enabled) return;
    
    // Storage for active chat sessions
    this.chatSessions = this.loadChatSessions();
    
    this.initialize();
    
    // Log active sessions if debugging is enabled
    if (this.config.debug) {
      console.log('ProductChatConversionTracker initialized with sessions:', this.chatSessions);
    }
  }
  
  /**
   * Initialize event listeners for Shopify customer events
   */
  initialize() {
    // Listen for add to cart events
    document.addEventListener('product:added', this.handleAddToCart.bind(this));
    
    // Listen for checkout begin events
    document.addEventListener('checkout:created', this.handleCheckoutCreated.bind(this));
    
    // Handle order completion - this varies based on Shopify setup
    // Common approach is to check the thank you page
    if (window.location.pathname.includes('/thank_you') || 
        window.location.pathname.includes('/orders/')) {
      this.handleOrderCompleted();
    }
    
    // Listen for checkout success event if using Shopify Customer Events API
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.subscribe) {
        window.Shopify.analytics.subscribe('checkout_completed', (event) => {
          this.handleShopifyCheckoutEvent(event);
        });
      }
    } catch (e) {
      console.error('Error setting up Shopify analytics listeners:', e);
    }
  }
  
  /**
   * Load stored chat sessions from localStorage
   */
  loadChatSessions() {
    try {
      // Use the accessible ProductChatTracking global object if available
      if (window.ProductChatTracking && window.ProductChatTracking.getSessionData) {
        return window.ProductChatTracking.getSessionData();
      }
      
      // Fallback to direct localStorage access
      const data = localStorage.getItem(this.config.storageKey);
      return data ? JSON.parse(data) : {};
    } catch (e) {
      console.error('Error loading chat sessions:', e);
      return {};
    }
  }
  
  /**
   * Handle product added to cart event
   */
  handleAddToCart(event) {
    if (!event || !event.detail) return;
    
    try {
      const productData = event.detail;
      const productId = productData.id || '';
      const variantId = productData.variant_id || '';
      
      if (this.config.debug) {
        console.log('Product added to cart:', productData);
      }
      
      // Find matching chat sessions for this product
      const matchingSessions = this.findMatchingSessions(productId, variantId);
      
      if (matchingSessions.length > 0) {
        // Track the add to cart event
        this.trackEvent('add_to_cart', matchingSessions[0], productData);
      }
    } catch (e) {
      console.error('Error handling add to cart event:', e);
    }
  }
  
  /**
   * Handle checkout created event
   */
  handleCheckoutCreated(event) {
    if (!event || !event.detail) return;
    
    try {
      const checkoutData = event.detail;
      
      if (this.config.debug) {
        console.log('Checkout created:', checkoutData);
      }
      
      // We need to handle line items to match products
      if (checkoutData.line_items) {
        checkoutData.line_items.forEach(item => {
          const productId = item.product_id || '';
          const variantId = item.variant_id || '';
          
          // Find matching chat sessions
          const matchingSessions = this.findMatchingSessions(productId, variantId);
          
          if (matchingSessions.length > 0) {
            // Track the checkout event
            this.trackEvent('begin_checkout', matchingSessions[0], {
              checkout_id: checkoutData.id || '',
              product_id: productId,
              variant_id: variantId,
              quantity: item.quantity || 1
            });
          }
        });
      }
    } catch (e) {
      console.error('Error handling checkout event:', e);
    }
  }
  
  /**
   * Handle order completed (thank you page)
   */
  handleOrderCompleted() {
    try {
      // Extract order information from the page
      const orderDetails = this.extractOrderDetails();
      
      if (!orderDetails || !orderDetails.order_id) {
        if (this.config.debug) {
          console.log('No order details found on page');
        }
        return;
      }
      
      if (this.config.debug) {
        console.log('Order completed:', orderDetails);
      }
      
      // Process each line item
      if (orderDetails.line_items) {
        orderDetails.line_items.forEach(item => {
          // Find matching chat sessions
          const matchingSessions = this.findMatchingSessions(item.product_id, item.variant_id);
          
          if (matchingSessions.length > 0) {
            // Create a conversion event
            this.trackConversion(matchingSessions[0], {
              order_id: this.config.testMode ? 
                `${this.config.testOrderPrefix}${orderDetails.order_id}` : 
                orderDetails.order_id,
              product_id: item.product_id,
              variant_id: item.variant_id,
              price: item.price,
              quantity: item.quantity,
              total: item.price * item.quantity
            });
          }
        });
      }
    } catch (e) {
      console.error('Error handling order completed:', e);
    }
  }
  
  /**
   * Handle Shopify checkout completion event
   */
  handleShopifyCheckoutEvent(event) {
    try {
      if (!event || !event.checkout) return;
      
      const checkout = event.checkout;
      const orderId = checkout.order_id || checkout.id;
      
      if (this.config.debug) {
        console.log('Shopify checkout completed:', checkout);
      }
      
      // Process each line item
      if (checkout.line_items) {
        checkout.line_items.forEach(item => {
          const productId = item.product_id;
          const variantId = item.variant_id;
          
          // Find matching chat sessions
          const matchingSessions = this.findMatchingSessions(productId, variantId);
          
          if (matchingSessions.length > 0) {
            // Create a conversion event
            this.trackConversion(matchingSessions[0], {
              order_id: this.config.testMode ? 
                `${this.config.testOrderPrefix}${orderId}` : 
                orderId,
              product_id: productId,
              variant_id: variantId,
              price: item.price,
              quantity: item.quantity,
              total: item.price * item.quantity
            });
          }
        });
      }
    } catch (e) {
      console.error('Error handling Shopify checkout event:', e);
    }
  }
  
  /**
   * Extract order details from the thank you page
   */
  extractOrderDetails() {
    try {
      // First try to get from Shopify global object
      if (window.Shopify && window.Shopify.checkout) {
        return {
          order_id: window.Shopify.checkout.order_id,
          order_number: window.Shopify.checkout.order_number,
          line_items: window.Shopify.checkout.line_items
        };
      }
      
      // Try to extract from JSON in the page
      const jsonScripts = document.querySelectorAll('script[type="application/json"]');
      for (let i = 0; i < jsonScripts.length; i++) {
        try {
          const data = JSON.parse(jsonScripts[i].textContent);
          if (data && (data.order || data.checkout)) {
            const order = data.order || data.checkout;
            if (order.order_id || order.id) {
              return {
                order_id: order.order_id || order.id,
                order_number: order.order_number || order.name,
                line_items: order.line_items || []
              };
            }
          }
        } catch (e) {
          // Skip this script tag if it's not valid JSON
        }
      }
      
      // Extract from the DOM if needed
      // This is very theme-specific, so we'll use a simple approach
      const orderIdEl = document.querySelector('[data-order-id]');
      const orderNumberEl = document.querySelector('[data-order-number]');
      
      if (orderIdEl || orderNumberEl) {
        // For line items, we need to get creative
        // This is highly theme-dependent, so we'll use a generic approach
        const lineItemElements = document.querySelectorAll('.line-item, .product-item, .product');
        const lineItems = [];
        
        lineItemElements.forEach(item => {
          // Try to extract product ID, variant ID, etc.
          // This is just an example - real implementation depends on theme
          const productId = item.getAttribute('data-product-id');
          const variantId = item.getAttribute('data-variant-id');
          const priceEl = item.querySelector('.price');
          const quantityEl = item.querySelector('.quantity');
          
          if (productId) {
            lineItems.push({
              product_id: productId,
              variant_id: variantId || productId,
              price: priceEl ? parseFloat(priceEl.textContent.replace(/[^0-9.]/g, '')) : 0,
              quantity: quantityEl ? parseInt(quantityEl.textContent) : 1
            });
          }
        });
        
        return {
          order_id: orderIdEl ? orderIdEl.getAttribute('data-order-id') : 
            orderNumberEl.getAttribute('data-order-number'),
          order_number: orderNumberEl ? orderNumberEl.getAttribute('data-order-number') : 
            orderIdEl.getAttribute('data-order-id'),
          line_items: lineItems
        };
      }
      
      return null;
    } catch (e) {
      console.error('Error extracting order details:', e);
      return null;
    }
  }
  
  /**
   * Find matching chat sessions for a product
   */
  findMatchingSessions(productId, variantId) {
    if (!productId) return [];
    
    const matchingSessions = [];
    const now = Date.now();
    
    // Convert product and variant IDs to strings for comparison
    const pId = String(productId);
    const vId = variantId ? String(variantId) : null;
    
    Object.values(this.chatSessions).forEach(session => {
      // Check if session is expired
      if (session.expiresAt < now) return;
      
      // Check for product ID match
      if (String(session.productId) === pId) {
        // If variant ID is specified, check for that too
        if (!vId || String(session.variantId) === vId) {
          matchingSessions.push(session);
        }
      }
    });
    
    return matchingSessions;
  }
  
  /**
   * Track a general event
   */
  trackEvent(eventType, session, eventData) {
    if (this.config.debug) {
      console.log(`Tracking ${eventType} event for session ${session.sessionId}:`, eventData);
    }
    
    // In test mode, we just log the event
    if (this.config.testMode) {
      console.log(`TEST MODE: ${eventType} event for session ${session.sessionId}`, eventData);
      return;
    }
    
    // You could send this to your analytics system or the Google Sheet
    // For now, we'll just log it to the console
  }
  
  /**
   * Track a conversion event (order completed)
   */
  trackConversion(session, conversionData) {
    if (this.config.debug) {
      console.log(`Tracking conversion for session ${session.sessionId}:`, conversionData);
    }
    
    // Prepare data to send to the Google Sheet
    const payload = {
      sessionId: session.sessionId,
      conversionType: 'purchase',
      orderId: conversionData.order_id,
      productId: conversionData.product_id,
      variantId: conversionData.variant_id,
      price: conversionData.price,
      quantity: conversionData.quantity,
      total: conversionData.total,
      timestamp: new Date().toISOString(),
      testMode: this.config.testMode
    };
    
    // In test mode, we log but don't send unless testMode flag is explicitly set
    if (this.config.testMode) {
      console.log('TEST MODE: Conversion event would be sent to Google Sheet:', payload);
      
      // We still want to send the data in test mode, just marked as a test
      this.sendConversionToSheet(payload);
      return;
    }
    
    // Send the conversion data to the Google Sheet
    this.sendConversionToSheet(payload);
  }
  
  /**
   * Send conversion data to the Google Sheet
   */
  sendConversionToSheet(conversionData) {
    try {
      fetch(this.config.conversionEventEndpoint, {
        method: 'POST',
        mode: 'no-cors', // Important for cross-origin requests to Google Apps Script
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(conversionData)
      })
      .then(() => {
        if (this.config.debug) {
          console.log('Conversion data sent to Google Sheet');
        }
      })
      .catch(error => {
        console.error('Error sending conversion data:', error);
      });
    } catch (e) {
      console.error('Error sending conversion data:', e);
    }
  }
}

// Initialize the conversion tracker when the DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize with default configuration
  new ProductChatConversionTracker();
}); 